import React from "react";
import { Card, Row, Col, Typography, Space } from "antd";
import {
    PlusOutlined,
    MinusOutlined,
    SwapOutlined,
    ClockCircleOutlined,
    SyncOutlined,
    PauseCircleOutlined,
    PlayCircleOutlined,
    CalendarOutlined,
    StopOutlined,
    EditOutlined,
    CopyOutlined,
    DeleteOutlined
} from "@ant-design/icons";
import Incorporation from "../avenantsModalComponents/Incorporation";
import RetraitVehicule from "../avenantsModalComponents/RetraitVehicule";
import AugmentationDeGarantie from "../avenantsModalComponents/AugmentationDeGarantie";
import DiminutionDeGarantie from "../avenantsModalComponents/DiminutionDeGarantie";
import Prorogation from "../avenantsModalComponents/Prorogation";
import SuspensionContrat from "../avenantsModalComponents/SuspensionContrat";
import RemiseEnVigueur from "../avenantsModalComponents/RemiseEnVigueur";
import ReportEffet from "../avenantsModalComponents/ReportEffet";
import TransfertDeGaranties from "../avenantsModalComponents/TransfertDeGaranties";
import ChangementDeVehicule from "../avenantsModalComponents/ChangementDeVehicule";
import Renouvellement from "../avenantsModalComponents/Renouvellement";
import AvenantDOrdre from "../avenantsModalComponents/AvenantDOrdre";
import AvenantDOrdreEngin from "../avenantsModalComponents/AvenantDOrdreEngin";
import Duplicata from "../avenantsModalComponents/Duplicata";
import Resiliation from "../avenantsModalComponents/Resiliation";
import { toast } from "react-toastify";
import { isAfterToday } from "../../../../variables/datesFunctions";
import { frenchDate } from "../../../../variables/functions";
import { LOGGED_USER } from "../../../../variables/constants";

const { Title, Text } = Typography;

const AvenantsTab = ({
    contrat,
    modalIncorporation,
    modalRetrait,
    modalAugmentation,
    modalDiminution,
    modalProrogation,
    modalSuspension,
    modalRemiseEnVigueur,
    modalReportEffet,
    modalTransfertGarantie,
    modalChangementVehicule,
    modalRenouvellement,
    modalAvenantOrdre,
    modalAvenantOrdreEngin,
    modalDuplicata,
    modalResiliation,
    updateIncorporationModal,
    updateRetraitModal,
    updateAugmentationModal,
    updateDiminutionModal,
    updateProrogationModal,
    updateSuspensionModal,
    updateRemiseEnVigueurModal,
    updateReportEffetModal,
    updateTransfertGarantieModal,
    updateChangementVehiculeModal,
    updateRenouvellementModal,
    updateAvenantOrdreModal,
    updateAvenantOrdreEnginModal,
    updateDuplicataModal,
    updateResiliationModal
}) => {
    const primaryColor = "#1890ff";
    const secondaryColor = "#242166";
    const successColor = "#52c41a";
    const warningColor = "#faad14";
    const dangerColor = "#ff4d4f";
    const infoColor = "#13c2c2";

    const isUserConnected = !!LOGGED_USER;

    const avenantCardStyle = {
        borderRadius: "12px",
        height: "100%",
        transition: "all 0.3s ease",
        border: `1px solid ${secondaryColor}15`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        cursor: "pointer"
    };

    const headerStyle = {
        background: `linear-gradient(135deg, ${secondaryColor} 0%, ${primaryColor} 100%)`,
        color: "white",
        padding: "16px 20px",
        borderRadius: "12px 12px 0 0",
        minHeight: "80px",
        display: "flex",
        alignItems: "center"
    };

    const buttonStyle = {
        borderRadius: "8px",
        height: "40px",
        fontWeight: 500,
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        width: "100%",
        marginTop: "auto"
    };

    const withUserCheck = (action) => () => {
        if (!isUserConnected) {
            toast.error("Votre session a expiré. Veuillez vous reconnecter pour gérer les avenants.");
            return;
        }
        // Le siège peut intervenir pour toute agence, les autres conservent le comportement existant
        action();
    };

    const AvenantCard = ({ title, description, icon, color, onClick, badge, disabled = false }) => (
        <Card
            hoverable={!disabled}
            style={{
                ...avenantCardStyle,
                opacity: disabled ? 0.6 : 1,
                cursor: disabled ? "not-allowed" : "pointer"
            }}
            bodyStyle={{
                padding: 0,
                height: "100%",
                display: "flex",
                flexDirection: "column"
            }}
            onClick={disabled ? undefined : onClick}
        >
            <div style={headerStyle}>
                <Space size="middle" style={{ width: "100%" }}>
                    <div style={{
                        fontSize: "32px",
                        background: "rgba(255,255,255,0.2)",
                        width: "56px",
                        height: "56px",
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}>
                        {icon}
                    </div>
                    <Title level={5} style={{ margin: 0, color: "white", flex: 1 }}>
                        {title}
                    </Title>
                </Space>
            </div>
            <div style={{
                padding: "20px",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between"
            }}>
                <Text style={{ color: "#666", lineHeight: "1.6", marginBottom: "16px" }}>
                    {description}
                </Text>
                {badge && (
                    <div style={{ marginTop: "12px", marginBottom: "16px" }}>
                        {badge}
                    </div>
                )}
            </div>
        </Card>
    );

    const contratEffetDansLeFutur = contrat?.dateEffet ? isAfterToday(contrat.dateEffet) : false;

    const avenants = [
        {
            show: contrat?.engins?.[0]?.categorie?.codeCategorie === "M" && isAfterToday(contrat?.dateEcheance),
            title: "Incorporation",
            description: "Ajouter un ou plusieurs nouveaux engins à la flotte existante",
            icon: <PlusOutlined />,
            color: successColor,
            onClick: withUserCheck(() => updateIncorporationModal(true))
        },
        {
            show: contrat?.engins?.[0]?.categorie?.codeCategorie === "M" && isAfterToday(contrat?.dateEcheance) && contrat?.engins?.length > 1,
            title: "Retrait",
            description: "Retirer un engin de la flotte assurée",
            icon: <MinusOutlined />,
            color: dangerColor,
            onClick: withUserCheck(() => updateRetraitModal(true))
        },
        {
            show: isAfterToday(contrat?.dateEcheance),
            title: "Augmentation de Garantie",
            description: "Souscrire à une garantie supplémentaire non obligatoire sur le contrat en cours",
            icon: <PlusOutlined />,
            color: successColor,
            onClick: withUserCheck(() => {
                if (contrat?.suspendu) {
                    toast.error("Contrat suspendu, impossible d'augmenter la garantie");
                } else {
                    updateAugmentationModal(true);
                }
            })
        },
        {
            show: isAfterToday(contrat?.dateEcheance),
            title: "Diminution de Garantie",
            description: "Retirer des garanties supplémentaires du contrat en cours",
            icon: <MinusOutlined />,
            color: warningColor,
            onClick: withUserCheck(() => {
                if (contrat?.suspendu) {
                    toast.error("Contrat suspendu, impossible de diminuer la garantie");
                } else {
                    updateDiminutionModal(true);
                }
            })
        },
        {
            show: contrat?.fractionnement?.nombre !== 1 && isAfterToday(contrat?.dateEcheance),
            title: "Prorogation",
            description: "Proroger le contrat sur la période restante",
            icon: <ClockCircleOutlined />,
            color: infoColor,
            onClick: withUserCheck(() => {
                if (contrat?.suspendu) {
                    toast.error("Contrat suspendu, impossible de proroger");
                } else {
                    updateProrogationModal(true);
                }
            })
        },
        {
            show: true,
            title: "Renouvellement",
            description: "Renouveler le contrat à échéance",
            icon: <SyncOutlined />,
            color: primaryColor,
            onClick: withUserCheck(() => {
                if (contrat?.suspendu) {
                    toast.error("Contrat suspendu, impossible de renouveler");
                } else {
                    updateRenouvellementModal(true);
                }
            }),
            badge: contrat?.dateEcheance && (
                <Space direction="vertical" size="small">
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                        Doit renouveler avant le:
                    </Text>
                    <Text strong style={{ color: primaryColor }}>
                        {frenchDate(contrat?.dateEcheance)}
                    </Text>
                </Space>
            )
        },
        {
            show: isAfterToday(contrat?.dateEcheance),
            title: "Suspension",
            description: "Suspendre temporairement l'effet du contrat en cours",
            icon: <PauseCircleOutlined />,
            color: warningColor,
            onClick: withUserCheck(() => updateSuspensionModal(true))
        },
        {
            show: isAfterToday(contrat?.dateEcheance),
            title: "Remise en Vigueur",
            description: "Remettre en vigueur un contrat suspendu",
            icon: <PlayCircleOutlined />,
            color: successColor,
            onClick: withUserCheck(() => updateRemiseEnVigueurModal(true))
        },
        {
            show: isAfterToday(contrat?.dateEcheance),
            title: "Report d'Effet",
            description: "Reporter l'effet du contrat sur une date ultérieure",
            icon: <CalendarOutlined />,
            color: infoColor,
            onClick: withUserCheck(() => {
                if (contrat?.suspendu) {
                    toast.error("Contrat suspendu, impossible de reporter l'effet");
                } else {
                    updateReportEffetModal(true);
                }
            })
        },
        {
            show: isAfterToday(contrat?.dateEcheance),
            title: "Résiliation",
            description: contratEffetDansLeFutur
                ? "La résiliation sera disponible une fois la date d'effet atteinte."
                : "Résilier définitivement le contrat en cours",
            icon: <StopOutlined />,
            color: dangerColor,
            onClick: () => updateResiliationModal(true),
            disabled: contratEffetDansLeFutur,
            badge: contratEffetDansLeFutur ? (
                <Text type="secondary" strong>
                    Disponible après la date d'effet
                </Text>
            ) : null
        },
        {
            show: isAfterToday(contrat?.dateEcheance),
            title: "Transfert de Garantie",
            description: "Transférer des garanties supplémentaires d'un engin à un autre du même assuré",
            icon: <SwapOutlined />,
            color: infoColor,
            onClick: withUserCheck(() => {
                if (contrat?.suspendu) {
                    toast.error("Contrat suspendu, impossible de transférer les garanties");
                } else {
                    updateTransfertGarantieModal(true);
                }
            })
        },
        {
            show: contrat?.engins?.length > 1 && isAfterToday(contrat?.dateEcheance),
            title: "Changement de Véhicule",
            description: "Remplacer le véhicule assuré par un nouveau véhicule",
            icon: <SwapOutlined />,
            color: primaryColor,
            onClick: withUserCheck(() => {
                if (contrat?.suspendu) {
                    toast.error("Contrat suspendu, impossible de changer de véhicule");
                } else {
                    updateChangementVehiculeModal(true);
                }
            })
        },
        {
            show: isAfterToday(contrat?.dateEcheance),
            title: "Avenant d'Ordre",
            description: "Modifier les informations du souscripteur",
            icon: <EditOutlined />,
            color: secondaryColor,
            onClick: withUserCheck(() => updateAvenantOrdreModal(true))
        },
        {
            show: isAfterToday(contrat?.dateEcheance),
            title: "Avenant d'Ordre - Engin",
            description: "Modifier les informations de l'engin assuré",
            icon: <EditOutlined />,
            color: secondaryColor,
            onClick: withUserCheck(() => updateAvenantOrdreEnginModal(true))
        },
        {
            show: true,
            title: "Duplicata",
            description: "Réimprimer les documents d'assurance",
            icon: <CopyOutlined />,
            color: warningColor,
            onClick: withUserCheck(() => {
                if (contrat?.suspendu) {
                    toast.error("Contrat suspendu, impossible d'effectuer le duplicata");
                } else {
                    updateDuplicataModal(true);
                }
            })
        },
        {
            show: isAfterToday(contrat?.dateEcheance) && contratEffetDansLeFutur,
            title: "Annulation de Contrat",
            description: "Annuler définitivement un contrat dont la date d'effet est future",
            icon: <DeleteOutlined />,
            color: dangerColor,
            onClick: () => {
                toast.info("Utilisez ce module pour annuler le contrat avant sa prise d'effet.");
            },
            disabled: false,
            badge: (
                <Text type="secondary" strong>
                    Recommandé tant que le contrat n'a pas pris effet
                </Text>
            )
        }
    ];

    return (
        <div style={{
            background: "white",
            padding: "32px",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
        }}>
            <div style={{
                marginBottom: "32px",
                paddingBottom: "24px",
                borderBottom: `2px solid ${secondaryColor}20`
            }}>
                <Title level={3} style={{ margin: 0, color: secondaryColor }}>
                    Gestion des Avenants
                </Title>
                <Text type="secondary" style={{ fontSize: "16px" }}>
                    Sélectionnez le type d'avenant à appliquer au contrat
                </Text>
            </div>

            <Row gutter={[24, 24]}>
                {avenants.filter(av => av.show).map((avenant, index) => (
                    <Col xs={24} sm={12} lg={8} key={index}>
                        <AvenantCard {...avenant} />
                    </Col>
                ))}
            </Row>

            {/* Modals */}
            <Incorporation
                open={modalIncorporation}
                update={updateIncorporationModal}
                contrat={contrat}
            />
            <RetraitVehicule
                open={modalRetrait}
                update={updateRetraitModal}
                contrat={contrat}
            />
            <AugmentationDeGarantie
                open={modalAugmentation}
                update={updateAugmentationModal}
                contrat={contrat}
            />
            <DiminutionDeGarantie
                open={modalDiminution}
                update={updateDiminutionModal}
                contrat={contrat}
            />
            <Prorogation
                open={modalProrogation}
                update={updateProrogationModal}
                contrat={contrat}
            />
            <SuspensionContrat
                open={modalSuspension}
                update={updateSuspensionModal}
                contrat={contrat}
            />
            <RemiseEnVigueur
                open={modalRemiseEnVigueur}
                update={updateRemiseEnVigueurModal}
                contrat={contrat}
            />
            <ReportEffet
                open={modalReportEffet}
                update={updateReportEffetModal}
                contrat={contrat}
            />
            <TransfertDeGaranties
                open={modalTransfertGarantie}
                update={updateTransfertGarantieModal}
                contrat={contrat}
            />
            <ChangementDeVehicule
                open={modalChangementVehicule}
                update={updateChangementVehiculeModal}
                contrat={contrat}
            />
            <Renouvellement
                open={modalRenouvellement}
                update={updateRenouvellementModal}
                contrat={contrat}
            />
            <AvenantDOrdre
                open={modalAvenantOrdre}
                update={updateAvenantOrdreModal}
                contrat={contrat}
            />
            <AvenantDOrdreEngin
                open={modalAvenantOrdreEngin}
                update={updateAvenantOrdreEnginModal}
                contrat={contrat}
            />
            <Duplicata
                open={modalDuplicata}
                update={updateDuplicataModal}
                contrat={contrat}
            />
            <Resiliation
                open={modalResiliation}
                update={updateResiliationModal}
                contrat={contrat}
            />
        </div>
    );
};

export default AvenantsTab;
